import React from "react";
import { CheckCircle, AlertCircle, Lock, EyeOff } from "lucide-react";
import { Badge } from "./Badge";

export interface StatusIndicatorProps {
  /** Whether the sensitive data is configured/stored */
  isConfigured: boolean;
  /** Label for the sensitive data type */
  label: string;
  /** Optional description */
  description?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Show lock icon */
  showLock?: boolean;
  /** Custom className */
  className?: string;
  /** Custom text for configured state (default: "Configured") */
  configuredText?: string;
  /** Custom text for not configured state (default: "Not Set") */
  notConfiguredText?: string;
}

/**
 * StatusIndicator - Shows the status of sensitive data without revealing the content
 * Used for displaying whether sensitive information (keys, passwords, etc.) is configured
 */
export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  isConfigured,
  label,
  description,
  size = "md",
  showLock = true,
  className = "",
  configuredText = "Configured",
  notConfiguredText = "Not Set",
}) => {
  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isConfigured
          ? "bg-green-500/10 border-green-500/20"
          : "bg-yellow-500/10 border-yellow-500/20"
      } ${className}`}
    >
      {/* Status Icon */}
      <div
        className={`flex items-center justify-center w-8 h-8 rounded-lg ${
          isConfigured ? "bg-green-500/20" : "bg-yellow-500/20"
        }`}
      >
        {isConfigured ? (
          <CheckCircle className={`${iconSizes[size]} text-green-400`} />
        ) : (
          <AlertCircle className={`${iconSizes[size]} text-yellow-400`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`font-medium ${
              isConfigured ? "text-green-300" : "text-yellow-300"
            } ${sizeClasses[size]}`}
          >
            {label}
          </span>
          <Badge variant={isConfigured ? "success" : "accent"} style="subtle" size="sm">
            {isConfigured ? configuredText : notConfiguredText}
          </Badge>
          {showLock && <Lock className={`${iconSizes[size]} text-slate-400`} />}
        </div>
        {description && <p className={`text-slate-400 ${sizeClasses[size]}`}>{description}</p>}
      </div>

      {/* Security Indicator */}
      <div className="flex items-center gap-1 text-slate-500">
        <EyeOff className={`${iconSizes[size]}`} />
        <span className={`${sizeClasses[size]} font-mono`}>
          {isConfigured ? "••••••••" : "--------"}
        </span>
      </div>
    </div>
  );
};

export default StatusIndicator;
